import React from 'react';
import { Redirect, Route, Router, Switch } from 'react-router';
import { useRecoilValue } from 'recoil';

import { DataInterfaceType } from '../gamma-api';

import { browserHistory } from './utils/historyUtil';
import LayoutBase from './layouts/LayoutBase';
import IbAccountWidgetWrapper from './wrappers/IbAccountWidgetWrapper';
import AppVersion from './components/AppVersion';
import ProjectsPage from './pages/projects/ProjectsPage';
import ProjectPage from './pages/projects/ProjectPage';
import ProvidersPage from './pages/providers/ProvidersPage';
import OcrProvidersPage from './pages/ocr-providers/OcrProvidersPage';
import AsrProvidersPage from './pages/asr-providers/AsrProvidersPage';
import SourcePage from './pages/projects/ProjectPage/sources/SourcePage';
import AgentsPage from './pages/agents/AgentsPage';
import AgentPage from './pages/agents/AgentPage';
import ApiKeysPage from './pages/api-keys/ApiKeysPage';
import ToolsetsPage from './pages/toolkits/elma365/ToolsetsPage';
import ToolsetPage from './pages/toolkits/elma365/ToolsetPage';
import ToolPage from './pages/toolkits/elma365/ToolPage';
import FoldersPage from './pages/folders/FoldersPage';
import { currentFolderIdSelector } from './recoil';
import {
  getApiAgentsPath,
  getApiKeysPath,
  getAsrPagePath,
  getChatAgentsPath,
  getFoldersPath,
  getOcrPagePath,
  getProjectsPath,
  getProvidersPath,
  getToolkitsPath,
} from './utils/pathUtils';

const goToMainPage = () => browserHistory.push('/');

const accountWidget = <IbAccountWidgetWrapper />;
const appVersion = <AppVersion />;

function App(): JSX.Element {
  const currentFolderId = useRecoilValue(currentFolderIdSelector);

  return (
    <Router history={browserHistory}>
      <Switch>
        <Route exact path="/folders/:folderId/agents">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getChatAgentsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <AgentsPage interfaceType={DataInterfaceType.Chat} />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/agents/:agentId/:tabKey?">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getChatAgentsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <AgentPage interfaceType={DataInterfaceType.Chat} />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/actions">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getApiAgentsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <AgentsPage interfaceType={DataInterfaceType.Api} />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/actions/:agentId/:tabKey?">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getApiAgentsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <AgentPage interfaceType={DataInterfaceType.Api} />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/projects">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getProjectsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <ProjectsPage />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/projects/:projectId/:tabKey?">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getProjectsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <ProjectPage />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/projects/:projectId/sources/:sourceId">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getProjectsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <SourcePage />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/toolkits/elma365">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getToolkitsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <ToolsetsPage />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/toolkits/elma365/:toolsetId">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getToolkitsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <ToolsetPage />
          </LayoutBase>
        </Route>
        <Route exact path="/folders/:folderId/toolkits/elma365/:toolsetId/tools/:toolId/:tabKey?">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getToolkitsPath(currentFolderId)}
            onGoToMainPage={goToMainPage}
          >
            <ToolPage />
          </LayoutBase>
        </Route>
        <Route exact path="/folders">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getFoldersPath()}
            onGoToMainPage={goToMainPage}
          >
            <FoldersPage />
          </LayoutBase>
        </Route>
        <Route exact path="/providers">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getProvidersPath()}
            onGoToMainPage={goToMainPage}
          >
            <ProvidersPage />
          </LayoutBase>
        </Route>
        <Route exact path="/ocr">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getOcrPagePath()}
            onGoToMainPage={goToMainPage}
          >
            <OcrProvidersPage />
          </LayoutBase>
        </Route>
        <Route exact path="/asr">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getAsrPagePath()}
            onGoToMainPage={goToMainPage}
          >
            <AsrProvidersPage />
          </LayoutBase>
        </Route>
        <Route exact path="/keys">
          <LayoutBase
            accountWidget={accountWidget}
            appVersion={appVersion}
            selectedItem={getApiKeysPath()}
            onGoToMainPage={goToMainPage}
          >
            <ApiKeysPage />
          </LayoutBase>
        </Route>
        <Route path="*">
          <Redirect to={getChatAgentsPath(currentFolderId)} />
        </Route>
      </Switch>
    </Router>
  );
}

export default App;
